import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, combineLatest, iif, of } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ThemeType } from 'src/app/samwebDescription/samweb/settings/theme-type';
import { DirectoryEditingBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-editing/directory-editing-behaviour.state';
import { PilotPanelState } from 'src/app/store/dashboard/pilot-panel/pilot-panel.state';
import { LoadChatGuestStatus, RemoveChat, StartLoadingChatDetails } from 'src/app/store/samwin/chats/chats.actions';
import { ChatsSelectors } from 'src/app/store/samwin/chats/chats.selectors';
import { ChatId } from 'src/app/store/samwin/chats/models/chat-id';
import { EmailBehaviourState } from 'src/app/store/samwin/email/email-behaviour/email-behaviour.state';
import { EndEmailProcess } from 'src/app/store/samwin/email/email-dialog/email-dialog-behaviour.actions';
import { SamwinAcdExtensionStateSelectors } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.selectors';
import { GetMessageContent } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.actions';
import { SamwinMessageLinesStateSelectors } from 'src/app/store/samwin/samwin-message-lines/samwin-message-lines.selectors';
import { ShortcutBehaviourState } from 'src/app/store/samwin/shortcut-behaviour/shortcut-behaviour.state';
import { CommunicationFailureSelectors } from 'src/app/store/server-communication/communication-failure.selectors';
import { SessionState } from 'src/app/store/session/session.state';
import { SetCurrentHotkeySettings } from 'src/app/store/settings/settings.actions';
import { SettingsState } from 'src/app/store/settings/settings.state';

@Component({
   selector: 'samweb-dashboard',
   templateUrl: './dashboard.component.html',
   styleUrls: ['./dashboard.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject();

   @Select(PilotPanelState.isPilotPanelExtended)
   public isPilotPanelExtended$: Observable<boolean>;

   @Select(SettingsState.appliedTheme)
   public applicationTheme$: Observable<ThemeType>;

   @Select(SettingsState.getBackgroundImageUrl)
   public backgroundImage$: Observable<string>;

   @Select(CommunicationFailureSelectors.isCommunicationHealthy)
   isCommunicationHealthy$: Observable<boolean>;

   @Select(EmailBehaviourState.getOpenEmailWindows)
   public openEmailWindows$: Observable<string[]>;

   @Select(ChatsSelectors.getOpenChats)
   public openChats$: Observable<ChatId[]>;

   @Select(DirectoryEditingBehaviourState.getEntriesInEditingIds)
   public directoryEntriesInEditing$: Observable<number>;

   constructor(private store: Store) {}

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   ngOnInit() {
      this.handleEmails();
      this.handleChats();
   }

   private handleEmails(): void {
      // NOTE: Cant use store init to subscribe, would cause circular dependency.
      combineLatest([
         this.store.select(SessionState.getIsAuthenticated),
         this.store.select(SamwinAcdExtensionStateSelectors.isAcdExtensionAssigned),
         this.store.select(ShortcutBehaviourState.areShortCutListsEqual),
      ])
         .pipe(
            distinctUntilChanged(),
            switchMap(([isAuthenticated, isAcdSelected, areShortCutListsEqual]) =>
               iif(
                  () => isAuthenticated && isAcdSelected && !areShortCutListsEqual,
                  this.store.dispatch(new SetCurrentHotkeySettings(this.store.selectSnapshot(ShortcutBehaviourState.getSettingsToUpdate))),
                  of(null)
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      const messagesToOpen$ = this.store.select(SamwinMessageLinesStateSelectors.getMessagesToOpen);
      const messagesToClose$ = this.store.select(SamwinMessageLinesStateSelectors.getMessagesToClose);

      messagesToOpen$
         .pipe(
            tap((messageIds) =>
               messageIds.forEach((messageId) => {
                  this.store.dispatch(new GetMessageContent(messageId));
               })
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      messagesToClose$
         .pipe(
            tap((messageIds) =>
               messageIds.forEach((messageId) => {
                  this.store.dispatch(new EndEmailProcess(messageId));
               })
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   private handleChats(): void {
      this.store
         .select(ChatsSelectors.getNotOpenChatsWithConnectedLine)
         .pipe(
            tap((chatIds) => {
               chatIds.forEach((chatId) => {
                  this.store.dispatch(new StartLoadingChatDetails(chatId, true));
                  this.store.dispatch(new LoadChatGuestStatus(chatId));
               });
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.store
         .select(ChatsSelectors.getOpenChatsWithoutConnectedLine)
         .pipe(
            tap((chatIds) => {
               chatIds.forEach((chatId) => {
                  this.store.dispatch(new RemoveChat(chatId));
               });
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }
}
